import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Building, 
  User, 
  Mail, 
  Phone, 
  Globe, 
  Calendar,
  UserCheck
} from "lucide-react";
import { CoinsIcon } from "@/components/ui/CoinsIcon";
import { ClientReferralWithUI } from "@/api/clientReferrals";
import { ClientReferralNote } from "@/lib/schema";
import { formatDateForSouthAfrica, formatRewardAmount } from "@/lib/textUtils";

interface ReferralInfoTabProps {
  referral: ClientReferralWithUI;
}

export function ReferralInfoTab({ referral }: ReferralInfoTabProps) {

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Information */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="space-grotesk text-lg flex items-center gap-2">
              <Building className="h-5 w-5 text-blue-600" />
              Company Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Building className="h-4 w-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-700 space-grotesk">Company Name</p>
                  <p className="text-gray-900 space-grotesk">{referral.companyName}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <User className="h-4 w-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-700 space-grotesk">Point of Contact</p>
                  <p className="text-gray-900 space-grotesk">{referral.pointOfContact}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-700 space-grotesk">Email</p>
                  <p className="text-gray-900 space-grotesk">{referral.email}</p>
                </div>
              </div>

              {referral.phoneNumber && (
                <div className="flex items-start gap-3">
                  <Phone className="h-4 w-4 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 space-grotesk">Phone Number</p>
                    <p className="text-gray-900 space-grotesk">{referral.phoneNumber}</p>
                  </div>
                </div>
              )}

              {referral.website && (
                <div className="flex items-start gap-3">
                  <Globe className="h-4 w-4 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 space-grotesk">Website</p>
                    <a 
                      href={referral.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 space-grotesk underline"
                    >
                      {referral.website}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Referral Details */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="space-grotesk text-lg flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-green-600" />
              Referral Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <UserCheck className="h-4 w-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-700 space-grotesk">Referred By</p>
                  <p className="text-gray-900 space-grotesk">{referral.referrerName}</p>
                  <p className="text-sm text-gray-500 space-grotesk">{referral.referrerEmail}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CoinsIcon className="h-4 w-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-700 space-grotesk">Reward Amount</p>
                  <p className="text-lg font-semibold space-grotesk">
                    R{formatRewardAmount(referral.rewardAmount)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="h-4 w-4 mt-1 flex items-center justify-center">
                  <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 space-grotesk">Priority</p>
                  <Badge className={`${getPriorityColor(referral.priority)} text-xs space-grotesk`}>
                    {referral.priority.toUpperCase()}
                  </Badge>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-700 space-grotesk">Created</p>
                  <p className="text-gray-900 space-grotesk">{formatDateForSouthAfrica(referral.createdAt)}</p>
                </div>
              </div>

              {referral.formattedUpdatedAt !== referral.formattedCreatedAt && (
                <div className="flex items-start gap-3">
                  <Calendar className="h-4 w-4 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 space-grotesk">Last Updated</p>
                    <p className="text-gray-900 space-grotesk">{referral.formattedUpdatedAt}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Information */}
      {referral.notes && referral.notes.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="space-grotesk text-lg">Additional Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {referral.notes.map((note: ClientReferralNote, index: number) => (
                <div key={index} className="border-l-4 border-l-blue-500 pl-4 py-2">
                  <p className="text-sm text-gray-700 space-grotesk">{note.note}</p>
                  <p className="text-xs text-gray-500 space-grotesk mt-1">
                    Added on {formatDateForSouthAfrica(note.date)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
